/**
 * CarsPagination.js
 * 
 * Componente per visualizzare auto con paginazione
 * 6-9 auto per pagina in base alla risoluzione
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Car } from 'lucide-react';

const ITEMS_PER_PAGE = 9; // 3x3 grid

const CarsPagination = ({ cars, renderCarCard }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Calcola il numero totale di pagine
  const totalPages = Math.ceil(cars.length / ITEMS_PER_PAGE);

  // Calcola gli indici delle auto da mostrare
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCars = cars.slice(startIndex, endIndex);

  // Gestisci il cambio di pagina
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll verso l'inizio della lista
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  // Se non ci sono auto
  if (cars.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl">
        <Car className="w-20 h-20 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-xl">Nessuna auto presente</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Griglia auto */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {currentCars.map((car) => renderCarCard(car))}
      </div>

      {/* Paginazione (solo se più di una pagina) */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-6">
          {/* Info pagina corrente */}
          <div className="text-sm text-gray-600">
            Pagina <span className="font-bold">{currentPage}</span> di{' '}
            <span className="font-bold">{totalPages}</span>
            <span className="ml-4 text-gray-500">
              ({startIndex + 1}-{Math.min(endIndex, cars.length)} di {cars.length} auto)
            </span>
          </div>

          {/* Controlli navigazione */}
          <div className="flex items-center gap-4">
            {/* Pulsante precedente */}
            <button
              onClick={goToPrevious}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Precedente
            </button>

            {/* Selezione pagina numerata */}
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition ${
                    page === currentPage
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                  title={`Vai a pagina ${page}`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Pulsante successivo */}
            <button
              onClick={goToNext}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Successivo
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Selezionatore rapido pagine (se più di 5 pagine) */}
          {totalPages > 5 && (
            <div className="flex items-center gap-2">
              <label htmlFor="pageSelect" className="text-sm text-gray-600">
                Vai a pagina:
              </label>
              <select
                id="pageSelect"
                value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <option key={page} value={page}>
                    Pagina {page}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CarsPagination;
